
import { defineComponent } from "vue";
import firebase from "firebase/app";
import "firebase/auth";
import { resolveProviderUrl } from "@/providerUrl";

const AUTH_SSO_COMPLETE_URL =
  process.env.VUE_APP_AUTH_SSO_COMPLETE_URL ||
  "https://us-central1-people2-admin.cloudfunctions.net/happilyTeamsSsoComplete";
const AUTH_SSO_ATTEMPT_PREVIEW_URL =
  process.env.VUE_APP_AUTH_SSO_ATTEMPT_PREVIEW_URL ||
  "https://us-central1-people2-admin.cloudfunctions.net/happilyTeamsSsoAttemptPreview";
const AUTH_SSO_CALLBACK_RECOVER_URL =
  process.env.VUE_APP_AUTH_SSO_CALLBACK_RECOVER_URL ||
  "https://us-central1-people2-admin.cloudfunctions.net/happilyTeamsSsoCallbackRecover";

type SsoState = {
  entityId: string;
  id: string;
  linkingUri: string;
  type: string;
};

const SSO_STATE_COOKIE_NAME = "ssoState";
const SSO_STATE_SESSION_STORAGE_KEY = "ssoState";
const SSO_ACCESS_CODE_SESSION_STORAGE_KEY = "ssoReadyAccessCode";

function createSsoState(
  entityId: string,
  searchParams: URLSearchParams
): SsoState {
  return {
    entityId,
    id: searchParams.get("id") || "",
    linkingUri: searchParams.get("linkingUri") || "",
    type: searchParams.get("type") || "",
  };
}

function getMissingSsoStateFields(state: SsoState): Array<keyof SsoState> {
  return (
    ["entityId", "id", "linkingUri", "type"] as Array<keyof SsoState>
  ).filter((field) => !state[field]);
}

export default defineComponent({
  name: "SAML",
  props: ["option"],
  data() {
    return {
      companyData: {
        name: "",
      },
      // 'working' | 'complete' | 'error'
      viewState: "working",
      redirectUri: "",
    };
  },
  async created() {
    // Old version for Firebase SAML Auth
    // this.initFirebaseSaml();

    const routeName = this.$route.name;
    if (routeName === "sso") {
      await this.initSsoReadySaml();
    } else if (routeName === "sso-callback") {
      await this.ssoCallback();
    } else if (routeName === "ssoready-callback") {
      await this.ssoreadyCallback();
    }
  },
  methods: {
    setCookie(cName: any, cValue: any, expMinutes: any) {
      let date = new Date();
      date.setTime(date.getTime() + expMinutes * 60 * 1000);
      const expires = "expires=" + date.toUTCString();
      document.cookie =
        cName +
        "=" +
        encodeURIComponent(cValue) +
        "; " +
        expires +
        "; Domain=.happily.ai; Path=/; Secure; SameSite=None";
    },
    getCookie(cName: any) {
      const name = cName + "=";
      const cArr = document.cookie.split("; ");
      let res;
      cArr.forEach((val) => {
        if (val.indexOf(name) === 0)
          res = decodeURIComponent(val.substring(name.length));
      });
      return res;
    },
    deleteCookie(cName: any) {
      document.cookie =
        cName +
        "=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Domain=.happily.ai; Path=/; Secure; SameSite=None";
    },
    clearStoredSsoState() {
      this.removeSessionStorageItem(SSO_STATE_SESSION_STORAGE_KEY);
      this.removeSessionStorageItem(SSO_ACCESS_CODE_SESSION_STORAGE_KEY);
      this.deleteCookie(SSO_STATE_COOKIE_NAME);
    },
    canUseSessionStorage() {
      try {
        const testKey = "__sso_session_storage_test__";
        window.sessionStorage.setItem(testKey, "1");
        window.sessionStorage.removeItem(testKey);
        return true;
      } catch {
        return false;
      }
    },
    getSessionStorageItem(key: string): string | null {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setSessionStorageItem(key: string, value: string) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {
        // Ignore sessionStorage write failures.
      }
    },
    removeSessionStorageItem(key: string) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {
        // Ignore sessionStorage remove failures.
      }
    },
    parseSsoStateFromUrl(entityId: string): SsoState {
      const currentUrl = new URL(window.location.href);
      return createSsoState(entityId, currentUrl.searchParams);
    },
    parseSsoStateFromQueryString(
      entityId: string,
      queryString: string
    ): SsoState {
      return createSsoState(entityId, new URLSearchParams(queryString));
    },
    serializeSsoState(state: SsoState) {
      return JSON.stringify(state);
    },
    parseSsoStateFromCookie(): SsoState | null {
      const serializedState = this.getCookie(SSO_STATE_COOKIE_NAME);
      if (serializedState) {
        try {
          const parsedState = JSON.parse(serializedState);
          return {
            entityId:
              typeof parsedState.entityId === "string"
                ? parsedState.entityId
                : "",
            id: typeof parsedState.id === "string" ? parsedState.id : "",
            linkingUri:
              typeof parsedState.linkingUri === "string"
                ? parsedState.linkingUri
                : "",
            type: typeof parsedState.type === "string" ? parsedState.type : "",
          };
        } catch {
          // Fallback to sessionStorage if cookie has invalid payload.
          return null;
        }
      }

      const entityId = this.getCookie("entityId") || "";
      const queryString = this.getCookie("queryString") || "";
      if (!entityId || !queryString) {
        return null;
      }

      return this.parseSsoStateFromQueryString(entityId, queryString);
    },
    parseSsoStateFromSessionStorage(): SsoState | null {
      const serializedState = this.getSessionStorageItem(
        SSO_STATE_SESSION_STORAGE_KEY
      );
      if (!serializedState) {
        return null;
      }

      try {
        const parsedState = JSON.parse(serializedState);
        return {
          entityId:
            typeof parsedState.entityId === "string"
              ? parsedState.entityId
              : "",
          id: typeof parsedState.id === "string" ? parsedState.id : "",
          linkingUri:
            typeof parsedState.linkingUri === "string"
              ? parsedState.linkingUri
              : "",
          type: typeof parsedState.type === "string" ? parsedState.type : "",
        };
      } catch {
        return null;
      }
    },
    buildSsoStateQueryString(state: SsoState) {
      return new URLSearchParams({
        id: state.id,
        linkingUri: state.linkingUri,
        type: state.type,
      }).toString();
    },
    async initFirebaseSaml() {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      const provider = new firebase.auth.SAMLAuthProvider(
        "saml." + this.option // !!!!!PUT YOUR PROVIDER ID HERE!!!!!
      );
      if (params["type"] === "saml-authenticate") {
        // Are we logging a user in?
        if (this.getCookie("redirecting") === "true") {
          // Has firebase redirected back here from an SSO page?
          this.setCookie("redirecting", "false", 60); // Reset the redirect cookie (1 hour expiration)
          firebase
            .auth()
            .getRedirectResult()
            .then(
              async (result: any) => {
                if (result.credential && result.user) {
                  let credential = result.credential;
                  const user = result.user;
                  let credentialJSON = JSON.stringify(credential.toJSON()); // Conver the auth token into a string so it can be passed back
                  await this.completeSsoLogin({
                    attemptId: params["id"],
                    credential: credentialJSON,
                    user,
                  });
                  window.location.href = params["linkingUri"];
                  // window.location.href =
                  //   params["linkingUri"] + "?credential=" + credentialJSON+'&user='+JSON.stringify(user); // Redirect back to the app
                }
              },
              function (error: any) {
                alert(
                  "Error with authenticating!\n" +
                    "Code:" +
                    error.code +
                    "\n" +
                    error
                );
              }
            );
        } else {
          this.setCookie("redirecting", "true", 60); // Remember that it is a redirect once the user signs in
          firebase.auth().signInWithRedirect(provider); // Go to the sign in page
        }
      } else if (params["linkingUri"].includes("saml-reauthenticate")) {
        // Are we reauthenticating?
        //TODO: find a way to implement
      } else if (params["linkingUri"].includes("saml-link")) {
        // Are we linking an existing user to a SAML SSO?
        //TODO: find a way to implement
      }
    },
    async completeSsoLogin(payload: {
      attemptId: string;
      credential: string;
      user: firebase.User;
    }) {
      if (!payload.attemptId) {
        throw new Error("Missing SSO attempt id");
      }

      const idToken = await payload.user.getIdToken();
      const response = await fetch(AUTH_SSO_COMPLETE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + idToken,
        },
        body: JSON.stringify({
          attemptId: payload.attemptId,
          credential: payload.credential,
          user: JSON.stringify(payload.user),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || "Failed to complete SSO login");
      }
    },
    async loadCompanyNameForAttempt(attemptId: string, entityId: string) {
      this.companyData.name = "";
      if (!attemptId) return;

      try {
        const response = await fetch(AUTH_SSO_ATTEMPT_PREVIEW_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ attemptId, entityId }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result?.error || "Failed to preview SSO attempt");
        }

        if (result?.companyName) {
          this.companyData.name = result.companyName;
        }
      } catch (error) {
        try {
          const message =
            error instanceof Error ? error.message : String(error);
          await this.postSsoLog("SSO attempt preview failed: " + message);
        } catch {
          // Keep SSO redirects non-blocking when preview/logging fails.
        }
      }
    },
    async initSsoReadySaml() {
      await this.postSsoLog("Entered initSsoReadySaml: Before");
      const entityId = resolveProviderUrl(
        this.$route.params,
        window.location.pathname
      );

      // Important: URL from mobile app and web app for use the same logic
      // const result = 'https://applink.happily.ai/loginsso/'+res.key;
      // Linking.openURL(`https://accounts.happily.ai/sso/saml/${option.spEntityId}?id=${res.key}&linkingUri=${result}&type=saml-authenticate`);

      try {
        if (!entityId) {
          throw new Error("Missing entityId (providerUrl)");
        }

        const ssoState = this.parseSsoStateFromUrl(entityId);
        const missingFields = getMissingSsoStateFields(ssoState);
        if (missingFields.length) {
          throw new Error(
            "Missing SSO state fields: " + missingFields.join(", ")
          );
        }
        const queryString = this.buildSsoStateQueryString(ssoState);

        await this.postSsoLog(
          [
            "initSsoReadySaml before setCookie",
            `hostname=${window.location.hostname}`,
            `protocol=${window.location.protocol}`,
            `entityId.length=${ssoState.entityId.length}`,
            `id.length=${ssoState.id.length}`,
            `linkingUri.length=${ssoState.linkingUri.length}`,
            `type.length=${ssoState.type.length}`,
            `queryString.length=${queryString.length}`,
          ].join("\n")
        );

        const ssoStateSerialized = this.serializeSsoState(ssoState);
        const canUseCookie = navigator.cookieEnabled;
        const canUseSessionStorage = this.canUseSessionStorage();

        if (canUseCookie) {
          this.setCookie(SSO_STATE_COOKIE_NAME, ssoStateSerialized, 60);
        }

        if (canUseSessionStorage) {
          this.setSessionStorageItem(
            SSO_STATE_SESSION_STORAGE_KEY,
            ssoStateSerialized
          );
        }

        if (!canUseCookie && !canUseSessionStorage) {
          throw new Error("Cookies and sessionStorage are not supported");
        }

        const ssoStateJsonLen = ssoStateSerialized.length;
        const setCookieOrSession = [
          canUseCookie ? "cookie" : null,
          canUseSessionStorage ? "sessionStorage" : null,
        ].filter(Boolean);

        await this.postSsoLog(
          [
            "initSsoReadySaml after ssoState set",
            `entityId.length=${ssoState.entityId.length}`,
            `entityId=${ssoState.entityId}`,
            `id.length=${ssoState.id.length}`,
            `id=${ssoState.id}`,
            `linkingUri.length=${ssoState.linkingUri.length}`,
            `type.length=${ssoState.type.length}`,
            `queryString.length=${queryString.length}`,
            `ssoState.json.length=${ssoStateJsonLen}`,
            `ssoState.storedIn=${setCookieOrSession.join(", ")}`,
          ].join("\n")
        );

        await this.loadCompanyNameForAttempt(ssoState.id, ssoState.entityId);

        const baseUrl = process.env.VUE_APP_API_URL || "";
        if (!baseUrl) {
          throw new Error("Missing VUE_APP_API_URL");
        }
        const redirectUrl =
          baseUrl + "/sso/saml/" + ssoState.entityId + "?" + queryString;
        await this.postSsoLog(
          [
            "Entered initSsoReadySaml: Before redirect",
            `redirectUrl=${redirectUrl}`,
            `ssoState.json.length=${this.serializeSsoState(ssoState).length}`,
          ].join("\n")
        );
        window.location.href = redirectUrl;
      } catch (error) {
        const errorStack = error instanceof Error ? error.stack || "" : "";
        await this.handleError(errorStack);
        this.viewState = "error";
      }

      await this.postSsoLog("Entered initSsoReadySaml: After");
    },
    async recoverSsoAttempt(attemptId: string, samlAccessCode: string) {
      const payloads = [];
      if (attemptId) {
        payloads.push({ attemptId });
      }
      if (samlAccessCode) {
        payloads.push({ samlAccessCode });
      }

      for (const payload of payloads) {
        try {
          const response = await fetch(AUTH_SSO_CALLBACK_RECOVER_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const result = await response.json().catch(() => ({}));
          if (response.ok && result && result.linkingUri) {
            return result;
          }
        } catch {
          // Try the next recovery source.
        }
      }

      return null;
    },
    async ssoCallback() {
      try {
        await this.postSsoLog("Entered sso-callback");

        const queryId = String(this.$route.query.id || "");
        const stashedAccessCode =
          this.getSessionStorageItem(SSO_ACCESS_CODE_SESSION_STORAGE_KEY) || "";

        // Local state exists when this browser also started the flow.
        let ssoState =
          this.parseSsoStateFromCookie() ||
          this.parseSsoStateFromSessionStorage();
        if (ssoState && getMissingSsoStateFields(ssoState).length) {
          ssoState = null;
        }
        // A server-echoed id outranks state left behind by an older attempt.
        if (ssoState && queryId && ssoState.id !== queryId) {
          ssoState = null;
        }

        let attemptId = "";
        let linkingUri = "";
        let companyName = "";

        if (ssoState) {
          attemptId = ssoState.id;
          linkingUri = ssoState.linkingUri;
        } else if (queryId || stashedAccessCode) {
          // This browser holds no state: it only finished the login (e.g. a
          // Conditional Access policy forced the user from Chrome into Edge
          // partway through). Recover the attempt from the server instead.
          const recovered = await this.recoverSsoAttempt(
            queryId,
            stashedAccessCode
          );
          if (recovered) {
            attemptId = String(recovered.attemptId || queryId);
            linkingUri = String(recovered.linkingUri || "");
            companyName = String(recovered.companyName || "");
          }
        }

        if (!linkingUri) {
          await this.postSsoLog(
            [
              "sso-callback could not resolve the attempt",
              `hasLocalState=${Boolean(ssoState)}`,
              `hasQueryId=${Boolean(queryId)}`,
              `hasStashedAccessCode=${Boolean(stashedAccessCode)}`,
            ].join("\n")
          );
          this.viewState = "error";
          return;
        }

        if (companyName) {
          this.companyData.name = companyName;
        } else if (ssoState) {
          await this.loadCompanyNameForAttempt(attemptId, ssoState.entityId);
        }

        await this.postSsoLog(
          [
            "sso-callback before final redirect",
            `attemptId=${attemptId}`,
            `linkingUri=${linkingUri}`,
            `recoveredWithoutLocalState=${!ssoState}`,
          ].join("\n")
        );

        // Avoid reusing stale state if the user returns later.
        this.clearStoredSsoState();

        this.redirectUri = linkingUri;
        this.viewState = "complete";
        window.location.href = linkingUri;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const errorStack =
          error instanceof Error ? error.stack || errorMessage : errorMessage;
        await this.postSsoLog(errorStack || "Unknown sso-callback error");
        this.viewState = "error";
      }
    },
    async handleError(errorStack: string) {
      // let ip = "";
      // try {
      //   const res = await fetch("https://api.ipify.org?format=json");
      //   const data = await res.json();
      //   ip = data.ip || "";
      // } catch {
      //   ip = "unknown";
      // }

      await this.postSsoLog(errorStack);
    },
    async postSsoLog(message: string) {
      // Avoid dumping all cookies (can be large / truncated on mobile Safari).
      // Instead, log only the SSO state payloads we care about.
      const ssoStateRaw = this.getCookie(SSO_STATE_COOKIE_NAME) || "";
      const ssoStateSessionRaw =
        this.getSessionStorageItem(SSO_STATE_SESSION_STORAGE_KEY) || "";

      let ssoStateParsed: Partial<SsoState> | null = null;
      if (ssoStateRaw) {
        try {
          const parsed = JSON.parse(ssoStateRaw);
          ssoStateParsed = {
            entityId:
              typeof parsed?.entityId === "string" ? parsed.entityId : "",
            id: typeof parsed?.id === "string" ? parsed.id : "",
            linkingUri:
              typeof parsed?.linkingUri === "string" ? parsed.linkingUri : "",
            type: typeof parsed?.type === "string" ? parsed.type : "",
          };
        } catch {
          ssoStateParsed = null;
        }
      }

      let ssoStateSessionParsed: Partial<SsoState> | null = null;
      if (ssoStateSessionRaw) {
        try {
          const parsed = JSON.parse(ssoStateSessionRaw);
          ssoStateSessionParsed = {
            entityId:
              typeof parsed?.entityId === "string" ? parsed.entityId : "",
            id: typeof parsed?.id === "string" ? parsed.id : "",
            linkingUri:
              typeof parsed?.linkingUri === "string" ? parsed.linkingUri : "",
            type: typeof parsed?.type === "string" ? parsed.type : "",
          };
        } catch {
          ssoStateSessionParsed = null;
        }
      }

      const ssoStateLen = ssoStateRaw ? String(ssoStateRaw).length : 0;
      const ssoStateSessionLen = ssoStateSessionRaw
        ? String(ssoStateSessionRaw).length
        : 0;

      const cookiesFormatted = [
        `    ssoState.cookie.length=${ssoStateLen}`,
        ssoStateParsed
          ? (() => {
              const entityIdLen = (ssoStateParsed.entityId || "").length;
              const idLen = (ssoStateParsed.id || "").length;
              const linkingUriLen = (ssoStateParsed.linkingUri || "").length;
              const typeLen = (ssoStateParsed.type || "").length;
              return `    ssoState.fields=entityId(${entityIdLen}) id(${idLen}) linkingUri(${linkingUriLen}) type(${typeLen})`;
            })()
          : "    ssoState.fields=(empty/invalid-json)",
        `    ssoState.sessionStorage.length=${ssoStateSessionLen}`,
        ssoStateSessionParsed
          ? (() => {
              const entityIdLen = (ssoStateSessionParsed.entityId || "").length;
              const idLen = (ssoStateSessionParsed.id || "").length;
              const linkingUriLen = (ssoStateSessionParsed.linkingUri || "")
                .length;
              const typeLen = (ssoStateSessionParsed.type || "").length;
              return `    ssoState.sessionStorage.fields=entityId(${entityIdLen}) id(${idLen}) linkingUri(${linkingUriLen}) type(${typeLen})`;
            })()
          : "    ssoState.sessionStorage.fields=(empty/invalid-json)",
      ].join("\n");

      const debugInfo = {
        currentUrl: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer || "(empty)",
        cookieEnabled: navigator.cookieEnabled,
        cookies: "\n" + cookiesFormatted,
        message,
      };

      const text = Object.entries(debugInfo)
        .map(([key, value]) => `*${key}*: ${value}`)
        .join("\n");

      try {
        const baseUrl = process.env.VUE_APP_API_URL || "";
        // keepalive lets the log survive an immediate page navigation.
        await fetch(baseUrl + "/sso/log", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: text }),
          keepalive: true,
        });
      } catch {
        // Logging must never break the sign-in flow.
      }
    },
    async ssoreadyCallback() {
      const currentPath = this.$route.path;
      const queryString = this.$route.fullPath.split("?")[1] || "";

      // The API redirects back to /sso/callback in THIS browser afterwards.
      // Stashing the access code here lets that page recover the attempt
      // even when the flow started in a different browser and no ssoState
      // cookie or sessionStorage exists locally.
      const samlAccessCode = String(
        this.$route.query.saml_access_code ||
          this.$route.query.samlAccessCode ||
          ""
      );
      if (samlAccessCode) {
        this.setSessionStorageItem(
          SSO_ACCESS_CODE_SESSION_STORAGE_KEY,
          samlAccessCode
        );
      }

      // Fire and forget: never delay the redemption redirect on logging.
      this.postSsoLog(
        "Entered ssoready-callback trampoline, hasSamlAccessCode=" +
          Boolean(samlAccessCode)
      );

      const baseUrl = process.env.VUE_APP_API_URL || "";
      const redirectUrl =
        baseUrl + currentPath + (queryString ? "?" + queryString : "");
      window.location.href = redirectUrl;
    },
  },
});
